import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  TextField,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  IconButton,
  Tooltip,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  LocationOn as LocationIcon,
  MyLocation as MyLocationIcon,
  Clear as ClearIcon,
  Home as HomeIcon,
  Business as BusinessIcon
} from '@mui/icons-material';
import api from '../../utils/api';

const ADDRESSNOW_KEY = process.env.REACT_APP_ROYAL_MAIL_ADDRESSNOW_KEY;

/** Build full UK address string from Royal Mail AddressNow populate event */
const buildAddressFromAddressNow = (addr) => {
  if (!addr || typeof addr !== 'object') return '';
  if (addr.FormattedAddress && typeof addr.FormattedAddress === 'string') {
    return addr.FormattedAddress.trim();
  }
  const parts = [
    addr.Line1,
    addr.Line2,
    addr.Line3,
    addr.Town,
    addr.County,
    addr.Postcode
  ].filter(Boolean);
  return parts.join(', ');
};

/** Stable IDs for Royal Mail AddressNow field mapping - must be explicit so the mapping wizard can find fields */
const ADDRESSNOW_FORM_ID = 'addressnow-form';
const ADDRESSNOW_SEARCH_INPUT_ID = 'addressnow-search-input';

const AddressMapSelector = ({ 
  value = '', 
  onChange, 
  label = 'Address',
  placeholder = 'Enter your business address...',
  required = false,
  error = false,
  helperText = '',
  disabled = false,
  addressType = 'business',
  onAddressTypeChange = () => {},
  /** Explicit ID for the address search input - use when multiple AddressMapSelectors on same page */
  addressFieldId = ADDRESSNOW_SEARCH_INPUT_ID
}) => {
  const [inputValue, setInputValue] = useState(value);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loading, setLoading] = useState(false);
  const [geocodeError, setGeocodeError] = useState('');
  const [coordinates, setCoordinates] = useState(null);
  const [mapUrl, setMapUrl] = useState('');
  
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);
  const previousValueRef = useRef(value);
  const debounceTimeoutRef = useRef(null);
  const addressNowInitialisedRef = useRef(false);

  const useAddressNow = Boolean(ADDRESSNOW_KEY);

  // Geocode address and update state (shared by AddressNow and manual entry)
  const geocodeAndUpdate = async (addressStr, keepAddressAsIs = false) => {
    if (!addressStr?.trim()) return;
    setLoading(true);
    setGeocodeError('');
    try {
      const response = await api.post('/api/geocode/address', { address: addressStr.trim() });
      if (response.data.success) {
        const { coordinates, formattedAddress } = response.data.data;
        setCoordinates(coordinates);
        const [lng, lat] = coordinates;
        setMapUrl(`https://www.google.com/maps?q=${lat},${lng}`);
        const displayAddress = keepAddressAsIs ? addressStr.trim() : (formattedAddress || addressStr.trim());
        setInputValue(displayAddress);
        if (onChange) onChange(displayAddress);
      }
    } catch (err) {
      setGeocodeError('Failed to geocode address. Please check and try again.');
    } finally {
      setLoading(false);
    }
  };

  // Initialise Royal Mail AddressNow (script loaded in index.js per getting-started guide)
  useEffect(() => {
    if (!useAddressNow || addressNowInitialisedRef.current) return;

    const initAddressNow = () => {
      if (typeof window.addressNow === 'undefined') return false;
      addressNowInitialisedRef.current = true;
      window.addressNow.listen('load', (control) => {
        control.listen('populate', (address) => {
          const fullAddress = buildAddressFromAddressNow(address);
          if (fullAddress) {
            geocodeAndUpdate(fullAddress, true); // Keep PAF format, geocode for lat/lng
          }
        });
      });
      window.addressNow.load();
      return true;
    };

    if (initAddressNow()) return;
    // Script may still be loading; poll briefly
    const interval = setInterval(() => {
      if (initAddressNow()) clearInterval(interval);
    }, 100);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useAddressNow]);

  // Fetch address suggestions from Google Maps
  const fetchSuggestions = async (query) => {
    if (!query || query.trim().length < 2) {
      setSuggestions([]);
      setShowSuggestions(false);
      return;
    }

    setLoading(true);
    try {
      const response = await api.get('/api/geocode/places', {
        params: { input: query.trim() }
      });

      if (response.data && response.data.success) {
        const predictions = response.data.data?.predictions || [];
        setSuggestions(predictions);
        if (predictions.length > 0) {
          setShowSuggestions(true);
        } else {
          setShowSuggestions(false);
        }
      } else {
        setSuggestions([]);
        setShowSuggestions(false);
      }
    } catch (error) {
      // Don't show error for suggestions - just continue without them
      setSuggestions([]);
      setShowSuggestions(false);
    } finally {
      setLoading(false);
    }
  };

  // Geocode address to get coordinates only (for suggestion selection)
  const geocodeAddressForCoordinates = async (address) => {
    setLoading(true);
    setGeocodeError('');
    
    try {
      const response = await api.post('/api/geocode/address', { address });
      
      if (response.data.success) {
        const { coordinates } = response.data.data;
        setCoordinates(coordinates);
        
        // Create Google Maps URL
        const [lng, lat] = coordinates;
        const mapsUrl = `https://www.google.com/maps?q=${lat},${lng}`;
        setMapUrl(mapsUrl);
        
        // Don't update the address text - keep the original selected address
      }
    } catch (error) {
      // Don't show error for pre-populated addresses - user can still edit
      setGeocodeError('');
    } finally {
      setLoading(false);
    }
  };

  // Update input when value prop changes
  useEffect(() => {
    // Only update if value actually changed
    if (value !== previousValueRef.current) {
      setInputValue(value);
      previousValueRef.current = value;
      
      // If value is pre-populated (e.g., from scraper), try to fetch suggestions and geocode
      if (value && value.trim().length > 2) {
        if (!useAddressNow) {
          fetchSuggestions(value.trim());
          geocodeAddressForCoordinates(value.trim());
        }
        // When useAddressNow: only geocode on selection (populate event), not on every keystroke
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // Handle input change with debounced geocoding
  const handleInputChange = async (event) => {
    if (!event || !event.target) {
      return;
    }
    
    const newValue = event.target.value || '';
    setInputValue(newValue);
    setGeocodeError('');
    
    // Update parent component
    if (onChange) {
      onChange(newValue);
    }

    // Clear suggestions if input is empty
    if (!newValue.trim()) {
      setSuggestions([]);
      setShowSuggestions(false);
      setCoordinates(null);
      setMapUrl('');
      return;
    }

    // Clear any existing timeout
    if (debounceTimeoutRef.current) {
      clearTimeout(debounceTimeoutRef.current);
    }

    // Show suggestions dropdown when user starts typing (Google Places only)
    if (!useAddressNow && newValue.trim().length > 2) {
      debounceTimeoutRef.current = setTimeout(async () => {
        await fetchSuggestions(newValue.trim());
      }, 300);
    } else if (!useAddressNow) {
      setSuggestions([]);
      setShowSuggestions(false);
    }
  };

  // Handle suggestion selection
  const handleSuggestionSelect = async (suggestion) => {
    if (!suggestion || !suggestion.description) {
      return;
    }
    
    const selectedAddress = suggestion.description;
    setInputValue(selectedAddress);
    setShowSuggestions(false);
    setSuggestions([]);
    
    // Update parent component immediately with the selected address
    if (onChange) {
      onChange(selectedAddress);
    }

    // Geocode the selected address for coordinates only (don't update the address text)
    await geocodeAddressForCoordinates(selectedAddress);
  };

  // Handle manual geocoding (when user presses enter)
  const handleManualGeocode = async () => {
    if (inputValue.trim()) {
      await geocodeAndUpdate(inputValue.trim());
    }
  };

  // Handle key press events
  const handleKeyPress = (event) => {
    if (event.key === 'Enter') {
      event.preventDefault();
      handleManualGeocode();
    }
  };

  // Clear address and coordinates
  const handleClear = () => {
    setInputValue('');
    setCoordinates(null);
    setMapUrl('');
    setGeocodeError('');
    setSuggestions([]);
    setShowSuggestions(false);
    
    if (onChange) {
      onChange('');
    }
  };

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if clicking on the input field or suggestions dropdown
      if (
        (inputRef.current && inputRef.current.contains(event.target)) ||
        (suggestionsRef.current && suggestionsRef.current.contains(event.target))
      ) {
        return;
      }
      
      // Close suggestions if clicking outside both input and suggestions
      if (showSuggestions) {
        setShowSuggestions(false);
      }
    };

    if (showSuggestions) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => document.removeEventListener('mousedown', handleClickOutside);
    }
  }, [showSuggestions]);

  return (
    <Box sx={{ position: 'relative', width: '100%' }}>
      {/* Address Type Selector */}
      <Box sx={{ mb: 2 }}>
        <FormControl fullWidth size="small">
          <InputLabel>Address Type</InputLabel>
          <Select
            value={addressType || 'business'}
            onChange={(e) => {
              if (e && e.target && e.target.value) {
                onAddressTypeChange(e.target.value);
              }
            }}
            label="Address Type"
            disabled={disabled}
          >
            <MenuItem value="business">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <BusinessIcon fontSize="small" />
                Business Address
              </Box>
            </MenuItem>
            <MenuItem value="private">
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <HomeIcon fontSize="small" />
                Private Address
              </Box>
            </MenuItem>
          </Select>
        </FormControl>
      </Box>

      {/* Privacy Notice for Private Address */}
      {addressType === 'private' && (
        <Alert severity="info" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>Privacy Notice:</strong> When private address is selected, we will hide your exact address on the couples map. 
            Don't worry, you will still be found in search results, but your location will only show your town/city and approximate distance.
          </Typography>
        </Alert>
      )}

      {/* Address Input Field - wrapped in form for AddressNow (per getting-started guide) */}
      <Box
        sx={{ position: 'relative' }}
        component={useAddressNow ? 'form' : 'div'}
        id={useAddressNow ? ADDRESSNOW_FORM_ID : undefined}
        name={useAddressNow ? ADDRESSNOW_FORM_ID : undefined}
        onSubmit={(e) => e.preventDefault()}
      >
        <TextField
          ref={inputRef}
          id={useAddressNow ? addressFieldId : undefined}
          name={useAddressNow ? addressFieldId : undefined}
          fullWidth
          inputProps={useAddressNow ? { id: addressFieldId, name: addressFieldId, 'data-addressnow': 'search', autoComplete: 'off' } : undefined}
          label={addressType === 'private' ? 'Private Address' : label}
          placeholder={
            useAddressNow
              ? 'Start typing your address or postcode...'
              : (addressType === 'private' ? 'Enter your private address...' : placeholder)
          }
          value={inputValue || ''}
          onChange={handleInputChange}
          onKeyPress={handleKeyPress}
          onFocus={() => {
            // Show suggestions when field is focused if there are suggestions available
            if (suggestions.length > 0) {
              setShowSuggestions(true);
            }
          }}
          required={required}
          error={error || !!geocodeError}
          helperText={
            (showSuggestions && suggestions.length > 0 && !useAddressNow) ? '' : (
              helperText || geocodeError || (
                useAddressNow
                  ? 'Royal Mail PAF – 29.5 million UK addresses. Press Enter if not found.'
                  : 'Can\'t find it? Type full address and press Enter.'
              )
            )
          }
          disabled={disabled}
          InputProps={{
            startAdornment: <LocationIcon sx={{ mr: 1, color: 'text.secondary' }} />,
            endAdornment: (
              <Box sx={{ display: 'flex', alignItems: 'center' }}>
                {loading && <CircularProgress size={20} sx={{ mr: 1 }} />}
                {inputValue && (
                  <Tooltip title="Clear address">
                    <IconButton size="small" onClick={handleClear} disabled={disabled}>
                      <ClearIcon />
                    </IconButton>
                  </Tooltip>
                )}
              </Box>
            )
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              position: 'relative',
              zIndex: 1
            },
            '& .MuiFormHelperText-root': {
              position: 'relative',
              zIndex: 1
            }
          }}
        />

        {/* Loading indicator for suggestions */}
        {loading && inputValue.trim().length > 2 && (
          <Paper
            sx={{
              position: 'absolute',
              top: '56px', // Position right below input field (label + input height)
              left: 0,
              right: 0,
              zIndex: 1300,
              p: 2,
              boxShadow: 3,
              bgcolor: 'background.paper',
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              border: '1px solid',
              borderColor: 'divider',
              borderTop: 'none',
              borderRadius: '0 0 4px 4px'
            }}
          >
            <CircularProgress size={16} />
            <Typography variant="body2" color="text.secondary">
              Searching for addresses...
            </Typography>
          </Paper>
        )}

        {/* Address Suggestions Dropdown (Google Places - hidden when using Royal Mail AddressNow) */}
        {!useAddressNow && showSuggestions && suggestions && suggestions.length > 0 && !loading && (
          <Paper
            ref={suggestionsRef}
            sx={{
              position: 'absolute',
              top: '56px', // Position right below input field (label + input height)
              left: 0,
              right: 0,
              zIndex: 1300,
              maxHeight: 200,
              overflow: 'auto',
              boxShadow: 3,
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'divider',
              borderTop: 'none',
              borderRadius: '0 0 4px 4px'
            }}
          >
            {suggestions.map((suggestion, index) => (
              <Box
                key={suggestion.place_id || index}
                sx={{
                  p: 1.5,
                  cursor: 'pointer',
                  '&:hover': { backgroundColor: 'action.hover' },
                  borderBottom: index < suggestions.length - 1 ? 1 : 0,
                  borderColor: 'divider'
                }}
                onClick={() => handleSuggestionSelect(suggestion)}
              >
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {suggestion?.structured_formatting?.main_text || suggestion?.description || 'Unknown address'}
                </Typography>
                {suggestion?.structured_formatting?.secondary_text && (
                  <Typography variant="caption" color="text.secondary">
                    {suggestion.structured_formatting.secondary_text}
                  </Typography>
                )}
              </Box>
            ))}
          </Paper>
        )}
      </Box>

      {/* Map Preview */}
      {coordinates && mapUrl && (
        <Box sx={{ mt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Typography variant="subtitle2" gutterBottom>
            📍 Location Preview
          </Typography>
          <Paper
            sx={{
              p: 2,
              backgroundColor: 'grey.50',
              border: '1px solid',
              borderColor: 'grey.300'
            }}
          >
            <Box
              component="a"
              href={mapUrl}
              target="_blank"
              rel="noopener noreferrer"
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: 'primary.main',
                '&:hover': { textDecoration: 'underline' }
              }}
            >
              <MyLocationIcon sx={{ mr: 0.5, fontSize: 16 }} />
              <Typography variant="body2">
                View on Google Maps
              </Typography>
            </Box>
          </Paper>
        </Box>
      )}

      {/* Error Display */}
      {geocodeError && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {geocodeError}
        </Alert>
      )}
    </Box>
  );
};

export default AddressMapSelector;
